// @ts-nocheck
/*
 * Sparkle cursor trail + falling dust.
 * The OS cursor is hidden and replaced by a small glowing dot. A row of warm
 * bokeh sparkles follows the dot — brightest/largest right at the cursor (the
 * "beginning" of the trail) and fading + shrinking along the path the cursor
 * took (the "end"). Each sparkle lingers ~0.5–1s, so the trail lags slightly
 * and follows every movement. Trail particles also shed smaller "dust" motes
 * that drift downward under gravity, like glittering fallout.
 *
 * Palette is sampled from the warm gold/amber/orange reference bokeh.
 *
 * Performance: a single fixed canvas with pointer-events:none. Particles are
 * capped, the render loop pauses when idle and when the tab is hidden.
 * Disabled for touch / coarse pointers and for prefers-reduced-motion.
 */
(function () {
  "use strict";

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  if (reduceMotion || !finePointer) return;

  // Warm palette, ordered bright -> dark. The trail blends across these by
  // age: freshest particles near the cursor use the bright end, and they shift
  // toward orange/ember as they fall behind into the tail.
  const PALETTE = [
    [254, 251, 222], // #FEFBDE near-white gold
    [251, 231, 110], // #FBE76E soft gold
    [247, 166, 51], //  #F7A633 amber
    [246, 136, 45], //  #F6882D orange
    [213, 72, 20], //   #D54814 deep ember
  ];

  // Scale the whole effect down on low-end hardware (few CPU cores / little
  // RAM) so it never competes with scrolling or interaction on slow devices.
  const lowPower =
    (navigator.hardwareConcurrency || 8) <= 4 || (navigator.deviceMemory || 8) <= 4;

  const MAX_PARTICLES = lowPower ? 260 : 600;
  const SPAWN_SPACING = lowPower ? 11 : 7; // px between sparkles along a fast movement
  const MAX_SPAWN_STEPS = lowPower ? 24 : 48; // cap sparkles emitted per pointer event (fast flicks)
  const DUST_PER_SPARKLE = lowPower ? 1.5 : 3; // average dust motes shed per trail dot
  const EDGE_PER_SPARKLE = lowPower ? 2 : 4; // scattered glitter flecks shed per trail dot
  // Elements that should trigger the interactive hover glint (mirrors pointer).
  const INTERACTIVE_SELECTOR =
    'a, button, [role="button"], [role="link"], input:not([type="hidden"]), ' +
    'select, textarea, label, summary, [onclick], [tabindex]:not([tabindex="-1"]), ' +
    '.cursor-pointer';

  // On low-end hardware, render at CSS resolution (DPR 1) so the full-screen
  // canvas fills a quarter of the pixels a retina device would, and cap the
  // frame rate below so it costs about half as much per second.
  const dpr = Math.min(window.devicePixelRatio || 1, lowPower ? 1 : 2);
  // ~38fps ceiling on low-end devices. Physics uses real elapsed time (dt), so
  // motion stays consistent — it's simply drawn on fewer frames.
  const FRAME_MIN_MS = lowPower ? 26 : 0;
  let canvas, ctx;
  let width = 0;
  let height = 0;
  let lastX = null;
  let lastY = null;
  let curX = null;
  let curY = null;
  let pointerInside = false;
  let overInteractive = false; // true while hovering a link/button/etc.
  let hoverPulse = 0; // 0..1 eased toward overInteractive for a smooth grow
  let rafId = null;
  let lastTs = 0;
  const particles = [];
  const dust = [];

  // Pre-baked particle sprites (built once in setup). Blitting a cached soft
  // dot with drawImage is far cheaper than creating a radial gradient — and
  // save()/restore() — per particle every frame, which was the single biggest
  // per-frame cost and the main reason slow devices choked.
  const SPRITE_STOPS = 12; // number of palette color buckets to bake
  const SPRITE_PX = 64; //    source sprite resolution (downscaled when drawn)
  let sprites = null;

  // Sample the palette gradient at t (0 = bright start, 1 = dark/orange end)
  // and return an "r, g, b" string. Each particle gets a small fixed offset so
  // the colors vary rather than all marching in lockstep.
  function colorAt(t) {
    const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
    const span = PALETTE.length - 1;
    const pos = clamped * span;
    const i = Math.min(span - 1, Math.floor(pos));
    const f = pos - i;
    const a = PALETTE[i];
    const b = PALETTE[i + 1];
    const r = Math.round(a[0] + (b[0] - a[0]) * f);
    const g = Math.round(a[1] + (b[1] - a[1]) * f);
    const bl = Math.round(a[2] + (b[2] - a[2]) * f);
    return `${r}, ${g}, ${bl}`;
  }

  function setup() {
    // Hide the native cursor everywhere; we draw our own dot instead.
    //
    // ...except while a modal <dialog> is open. showModal() paints in the
    // browser's TOP LAYER, above every ordinary element — including this
    // canvas. Inside a lightbox the replacement dot is therefore invisible
    // while the real cursor is still hidden, which leaves the viewer with no
    // pointer at all and no visible way to aim at the close button. Suspending
    // the rule hands the real cursor back, so each element's own cursor
    // (zoom-out on the backdrop, pointer on the close button) applies again.
    document.documentElement.classList.add("sparkle-cursor-active");
    const awake = ":not(.sparkle-cursor-link):not(:has(dialog:modal))";
    const hideStyle = document.createElement("style");
    hideStyle.textContent =
      ".sparkle-cursor-active" + awake + ", .sparkle-cursor-active" + awake + " * { cursor: none !important; }";
    document.head.appendChild(hideStyle);

    canvas = document.createElement("canvas");
    canvas.setAttribute("aria-hidden", "true");
    Object.assign(canvas.style, {
      position: "fixed",
      inset: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: "2147483646",
    });
    document.body.appendChild(canvas);
    ctx = canvas.getContext("2d");
    resize();
    buildSprites();

    window.addEventListener("resize", resize, { passive: true });
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerout", onOut, { passive: true });
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) stop();
    });
  }

  function resize() {
    width = window.innerWidth;
    height = window.innerHeight;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function onMove(e) {
    if (e.pointerType && e.pointerType !== "mouse") return;
    const x = e.clientX;
    const y = e.clientY;
    if (lastX === null) {
      lastX = x;
      lastY = y;
    }
    curX = x;
    curY = y;
    pointerInside = true;
    overInteractive = isInteractive(e.target);
    // Over clickable elements: restore the native pointer cursor and suppress
    // the sparkle effect (no new trail here, dot hidden in tick()).
    document.documentElement.classList.toggle("sparkle-cursor-link", overInteractive);
    if (!overInteractive) spawnAlong(lastX, lastY, x, y);
    lastX = x;
    lastY = y;
    start();
  }

  // True when the pointer is over a clickable/interactive element so the glint
  // grows into a soft pulsing halo (mirrors the browser's pointer cursor).
  function isInteractive(el) {
    return !!(el && el.closest && el.closest(INTERACTIVE_SELECTOR));
  }

  function onOut(e) {
    if (e.relatedTarget !== null) return;
    pointerInside = false;
    overInteractive = false;
    document.documentElement.classList.remove("sparkle-cursor-link");
    // If the render loop has already gone idle, clear the lingering cursor dot
    // so it doesn't stay painted at the edge after the pointer leaves.
    if (!rafId && ctx) ctx.clearRect(0, 0, width, height);
  }

  // Emit sparkles evenly along the segment travelled since the last event so
  // the trail stays continuous even on fast flicks.
  function spawnAlong(x0, y0, x1, y1) {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const dist = Math.hypot(dx, dy);
    const steps = Math.min(MAX_SPAWN_STEPS, Math.max(1, Math.round(dist / SPAWN_SPACING)));
    for (let i = 0; i < steps; i++) {
      if (particles.length >= MAX_PARTICLES) break;
      const t = i / steps;
      spawn(x0 + dx * t, y0 + dy * t);
    }
  }

  function spawn(x, y) {
    // Wider band of particles coming off the trail.
    const jitter = 14;
    const px = x + (Math.random() - 0.5) * jitter;
    const py = y + (Math.random() - 0.5) * jitter;
    particles.push({
      x: px,
      y: py,
      vx: (Math.random() - 0.5) * 0.15,
      vy: (Math.random() - 0.5) * 0.15,
      size: 0.8 + Math.random() * 1.6, // tiny dots, varied sizes
      tint: (Math.random() - 0.5) * 0.18, // gentle per-particle color variation
      life: 0,
      ttl: 500 + Math.random() * 450,
    });
    // Shed dust motes that fall away under gravity.
    const dustCount = Math.random() < DUST_PER_SPARKLE % 1 ? Math.ceil(DUST_PER_SPARKLE) : Math.floor(DUST_PER_SPARKLE);
    for (let i = 0; i < dustCount && dust.length < MAX_PARTICLES; i++) {
      spawnDust(x, y);
    }
    // Shed scattered glitter flecks that sit just off the ribbon, slightly
    // overlapping its edges — lots of them, lightly twinkling in place.
    const edgeCount = Math.random() < EDGE_PER_SPARKLE % 1 ? Math.ceil(EDGE_PER_SPARKLE) : Math.floor(EDGE_PER_SPARKLE);
    for (let i = 0; i < edgeCount && dust.length < MAX_PARTICLES; i++) {
      spawnEdge(x, y);
    }
  }

  // Tiny motes that scatter out from the trail and drift down under gravity.
  function spawnDust(x, y) {
    const spread = 22; // lateral scatter from the core line
    dust.push({
      x: x + (Math.random() - 0.5) * spread,
      y: y + (Math.random() - 0.5) * spread * 0.6,
      vx: (Math.random() - 0.5) * 0.7,
      vy: Math.random() * 0.25, // starts slow, gravity accelerates it
      size: 0.3 + Math.random() * 0.9, // smaller than trail dots, varied
      tint: 0.25 + Math.random() * 0.45, // dust spans gold -> amber
      life: 0,
      ttl: 900 + Math.random() * 900,
      twinkle: Math.random() * Math.PI * 2,
    });
  }

  // Scattered glitter flecks sprinkled around the ribbon. They sit further out
  // than the core band but still overlap its edges, barely move, and twinkle.
  function spawnEdge(x, y) {
    // Bias placement toward the edges: push the fleck out from centre by a
    // random distance so most land just outside the core band, overlapping it.
    const angle = Math.random() * Math.PI * 2;
    const radius = 10 + Math.random() * 30; // overlaps inner edge, reaches well out
    dust.push({
      x: x + Math.cos(angle) * radius,
      y: y + Math.sin(angle) * radius * 0.7,
      vx: (Math.random() - 0.5) * 0.15, // nearly stationary
      vy: Math.random() * 0.08,
      size: 0.25 + Math.random() * 0.7, // very fine flecks
      tint: 0.15 + Math.random() * 0.5,
      life: 0,
      ttl: 700 + Math.random() * 800,
      twinkle: Math.random() * Math.PI * 2,
    });
  }

  function tick(ts) {
    // Low-end frame-rate cap: skip drawing until enough time has elapsed, but
    // keep the loop alive so it resumes on the next eligible frame.
    if (FRAME_MIN_MS && lastTs && ts - lastTs < FRAME_MIN_MS) {
      rafId = requestAnimationFrame(tick);
      return;
    }
    const dt = lastTs ? Math.min(ts - lastTs, 48) : 16;
    lastTs = ts;
    ctx.clearRect(0, 0, width, height);

    // Particles + dust share a single composite state (set once) and are each
    // a cheap drawImage of a pre-baked sprite — no per-dot gradient or
    // save/restore.
    ctx.save();
    ctx.globalCompositeOperation = "lighter";

    // Trail dots.
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.life += dt;
      const k = p.life / p.ttl;
      if (k >= 1) {
        particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt * 0.06;
      p.y += p.vy * dt * 0.06;
      // The line stays an even gold along its length and simply fades out at
      // the tail — only a slight shift toward amber, no hot "flame" at the head.
      const alpha = 1 - k;
      const size = p.size * (1 - k * 0.45);
      drawSprite(p.x, p.y, size, alpha, 0.12 + k * 0.45 + p.tint);
    }

    // Falling dust.
    for (let i = dust.length - 1; i >= 0; i--) {
      const d = dust[i];
      d.life += dt;
      const k = d.life / d.ttl;
      if (k >= 1) {
        dust.splice(i, 1);
        continue;
      }
      d.vy += dt * 0.0009; // gravity
      d.x += d.vx * dt * 0.06;
      d.y += d.vy * dt * 0.06;
      d.twinkle += dt * 0.01;
      const alpha = (1 - k) * (0.6 + 0.4 * Math.sin(d.twinkle));
      drawSprite(d.x, d.y, d.size, alpha, d.tint + k * 0.3);
    }

    ctx.globalAlpha = 1;
    ctx.restore();

    // Custom cursor dot — hidden while over a clickable element (the native
    // pointer cursor shows there instead).
    if (pointerInside && curX !== null && !overInteractive) {
      drawCursorDot(curX, curY);
    }

    // Keep animating only while particles are still alive. Once everything has
    // faded we stop the loop entirely instead of repainting the full-screen
    // canvas every frame just because the pointer is inside the window. The
    // resting cursor dot stays painted on the canvas until the pointer moves
    // again (which restarts the loop via onMove -> start). This prevents a
    // perpetual full-viewport repaint that otherwise saturates the compositor
    // and janks scrolling / header resize / image painting.
    if (particles.length > 0 || dust.length > 0) {
      rafId = requestAnimationFrame(tick);
    } else {
      rafId = null;
      lastTs = 0;
    }
  }

  // Bake one soft radial dot per palette bucket into an offscreen canvas so the
  // render loop only has to blit them.
  function buildSprites() {
    sprites = [];
    for (let i = 0; i < SPRITE_STOPS; i++) {
      const color = colorAt(i / (SPRITE_STOPS - 1));
      const c = document.createElement("canvas");
      c.width = c.height = SPRITE_PX;
      const g = c.getContext("2d");
      const r = SPRITE_PX / 2;
      const grad = g.createRadialGradient(r, r, 0, r, r, r);
      grad.addColorStop(0, `rgba(${color}, 1)`);
      grad.addColorStop(1, `rgba(${color}, 0)`);
      g.fillStyle = grad;
      g.beginPath();
      g.arc(r, r, r, 0, Math.PI * 2);
      g.fill();
      sprites.push(c);
    }
  }

  // Blit a pre-baked soft dot. `t` picks the palette bucket, `alpha` fades it,
  // and `size` sets the radius (matching the old gradient radius of size * 2.4).
  // Must be called inside a `globalCompositeOperation = "lighter"` block.
  function drawSprite(x, y, size, alpha, t) {
    if (alpha <= 0.01 || !sprites) return;
    let idx = Math.round(t * (SPRITE_STOPS - 1));
    if (idx < 0) idx = 0;
    else if (idx >= SPRITE_STOPS) idx = SPRITE_STOPS - 1;
    const r = size * 2.4;
    ctx.globalAlpha = alpha < 1 ? alpha : 1;
    ctx.drawImage(sprites[idx], x - r, y - r, r * 2, r * 2);
  }

  function drawCursorDot(x, y) {
    ctx.save();

    // Soft additive outer glow (a warm bloom, brightest on darker areas).
    ctx.globalCompositeOperation = "lighter";
    const glowR = 16;
    const glow = ctx.createRadialGradient(x, y, 0, x, y, glowR);
    glow.addColorStop(0, `rgba(${colorAt(0.12)}, 0.38)`);
    glow.addColorStop(0.55, `rgba(${colorAt(0.28)}, 0.18)`);
    glow.addColorStop(1, `rgba(${colorAt(0.4)}, 0)`);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(x, y, glowR, 0, Math.PI * 2);
    ctx.fill();

    // Defined halo ring + core drawn normally so they stay visible on light
    // backgrounds too.
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = `rgba(${colorAt(0.32)}, 0.75)`;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(x, y, 9, 0, Math.PI * 2);
    ctx.stroke();

    // Bright core dot.
    ctx.fillStyle = `rgba(${colorAt(0.05)}, 0.95)`;
    ctx.beginPath();
    ctx.arc(x, y, 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  function start() {
    if (!rafId) {
      lastTs = 0;
      rafId = requestAnimationFrame(tick);
    }
  }

  function stop() {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    particles.length = 0;
    dust.length = 0;
    lastTs = 0;
    if (ctx) ctx.clearRect(0, 0, width, height);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setup, { once: true });
  } else {
    setup();
  }
})();
